/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute } from '@angular/router';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { BaseComponent } from '../../../../../core/components';
import { ErrorResponseModel } from '../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LogService, NotifyService, UserService, UtilsService } from '../../../../../core/services';
import { SupportApiService } from '../../../shared/http';
import { forkJoin, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-ticket-conversation',
    templateUrl: './ticket-conversation.component.html',
    styleUrls: ['./ticket-conversation.component.scss']
})

export class TicketConversationComponent extends BaseComponent {
    /**
     * Expose the button element to parent components
     */
    @ViewChild('commentWrapper') public commentsWrapper: ElementRef;

    /**
     * Loading state
     */
    public loading: boolean = true;

    /**
     * To store the ticket.
     */
    public upDateMessageForm: FormGroup;

    /**
     * Ticket structure.
     */
    public ticketDetails: any = {
        id: '',
        priority: '',
        type: '',
        product: '',
        adcName: '',
        status: '',
        subject: '',
        description: '',
        created: ''
    };

    /**
     * Show pagination buttons
     */
    public showMoreRows: boolean = false;

    /**
     * Stored list of comments for the view.
     */
    public comments: any[] = [];

    /**
     * Pagination for conversation view.
     */
    public pagination: any = {
        showMoreRows: false,
        nextParameter: undefined,
        backParameter: undefined
    };

    /**
     * Ticket Id.
     * @private
     */
    private readonly ticketId: string = '';

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private logService: LogService,
        private notifyService: NotifyService,
        private supportApiService: SupportApiService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        this.ticketId = this.activatedRoute.snapshot.paramMap.get('id');

        this.setUpPageData();
        this.setUpForm();
    }

    /**
     * Update ticket.
     */
    public updateTicket(): Observable<any> {
        return new Observable<any>(observe => {
            this.notifyService.muteLocalNotifications();
            const data = {
                // eslint-disable-next-line camelcase
                html_body: this.upDateMessageForm.value.message,
                uploads: []
            };
            const apiCalls = {
                createTicketComment: this.supportApiService.createTicketComment(this.ticketId, data),
                updateTicketStatus: this.supportApiService.updateTicket(this.ticketId, { status: 'open' })
            };
            forkJoin(apiCalls)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        if (data['createTicketComment']) {
                            this.upDateMessageForm.get('message').setValue('');
                            this.updateTicketConversation();
                            this.notifyService.unmuteLocalNotifications();
                            this.notifyService.success('Ticket was updated successfully');
                        }
                        observe.next();
                        observe.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.notifyService.unmuteLocalNotifications();
                        this.logService.error(
                            'TicketConversationComponent:updateTicket',
                            'Error updating the ticket' + error
                        );
                        observe.error(error);
                        observe.complete();
                    }
                });
        });
    }

    /**
     * Navigate forward through comments.
     */
    public nextPage(): void {
        this.updateTicketConversation('&page[after]=' + this.pagination.nextParameter);
    }

    /**
     * Navigate back through comments.
     */
    public backPage(): void {
        this.updateTicketConversation('&page[before]=' + this.pagination.backParameter);
    }

    /**
     * Update comments only after submission.
     * @param cursor
     * @private
     */
    private updateTicketConversation(cursor?: any): void {
        this.supportApiService.getTicketComments(this.ticketId, cursor ? cursor : '')
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.comments = this.populateComments(data.comments);
                    if (data.metadata.cursor?.after_cursor && data.metadata.cursor?.before_cursor) {
                        this.pagination = this.populatePagination(data.metadata);
                        this.loading = false;
                        const timerId = setTimeout(() => {
                            this.scrollCommentsToEnd();
                            clearTimeout(timerId);
                        }, 250);
                    }
                },
                error: (error: ErrorResponseModel) => {
                    this.logService.error('TicketConversationComponent:updateComments', 'Unable to get new comments. ' + error);
                }
            });
    }

    /**
     * Initial API call to get ticket details and comments for this ticket.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = {
            ticketDetails: this.supportApiService.getTicket(this.ticketId),
            comments: this.supportApiService.getTicketComments(this.ticketId)
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup: any): void => {
                    if (dataGroup['ticketDetails']?.ticket && dataGroup['comments']?.comments) {
                        this.ticketDetails = this.populateTicketData(dataGroup['ticketDetails'].ticket);
                        this.comments = this.populateComments(dataGroup['comments'].comments);
                        this.pagination = this.populatePagination(dataGroup['comments'].metadata);
                        this.loading = false;
                        const timerId = setTimeout(() => {
                            this.scrollCommentsToEnd();
                            clearTimeout(timerId);
                        }, 250);
                    } else {
                        this.logService.error('TicketConversationComponent:setUpPageData', 'Some data is missing for the page');
                    }
                },
                error: (error: ErrorResponseModel) => {
                    this.logService.error('TicketConversationComponent:setUpPageData', 'Error getting page data' + error);
                }
            });
    }

    /**
     * Populate ticket data
     * @param ticket
     * @private
     */
    private populateTicketData(ticket: any): any {
        return {
            id: this.ticketId,
            priority: ticket.priority,
            type: ticket.type,
            product: this.utilsService.getCustomFieldValue(ticket?.custom_fields, 'Product').substring(8),
            adcName: '',
            status: ticket.status,
            subject: ticket.subject,
            description: ticket.description,
            created: this.utilsService.createDateTimeString(ticket.created_at)
        };
    }

    /**
     * Populate comments variable.
     * @param comments
     * @private
     */
    private populateComments(comments: any): any[] {
        const formattedComments = [];
        for (const comment of comments) {
            const ticketAddress = comment.via?.source?.to?.address;
            const excludedAddresses = ['devlist@loadbalancer.org', 'portal@loadbalancer.org'];
            const isLbReply: boolean = ticketAddress?.includes('@loadbalancer.org') && !excludedAddresses.includes(ticketAddress);
            formattedComments.push({
                author: comment.author.name,
                isLbReply,
                timeStamp: this.utilsService.createDateTimeString(comment.created_at),
                body: comment.body,
                html: comment.html_body,
                avatar: this.setAvatar(comment.author.name)
            });
        }

        return formattedComments;
    }

    /**
     * Set the avatar class variable.
     * @private
     */
    private setAvatar(userName: string): any {
        const firstName = userName.split(' ')[0];
        const lastName = userName.split(' ')[1];

        return this.userService.createUserAvatarFromNames(firstName, lastName);
    }

    /**
     * Scroll comments contains to end.
     * @private
     */
    private scrollCommentsToEnd(): void {
        const element = this.commentsWrapper.nativeElement;
        element.scrollTop = element.scrollHeight;
    }

    /**
     * Check for pagination.
     * @param metadata
     * @private
     */
    private populatePagination(metadata: any): any {
        if (metadata?.cursor?.has_more) {
            return {
                showMoreRows: true,
                nextParameter: metadata?.cursor?.after_cursor,
                backParameter: metadata?.cursor?.before_cursor
            };
        }
    }

    /**
     * Set up the form for the page
     * @private
     */
    private setUpForm(): void {
        this.upDateMessageForm = this.formBuilder.group({
            message: [null, Validators.required],
        });
    }
}
